import React from 'react';
import { 
  FiEye, 
  FiTrash2, 
  FiBook, 
  FiCalendar, 
  FiCopy,
  FiMapPin,
  FiAward,
  FiCpu,
  FiUser,
  FiLayers,
  FiChevronRight
} from 'react-icons/fi';

const ClassList = ({ 
  classes = [], 
  departments = [], 
  subjects = {}, 
  onViewDetails, 
  onEdit, 
  onDelete, 
  isSemesterBased = false,
  semesters = [],
  onShowCopySection,
  onFacultyAssignment,
  onLabBatchManagement,
  loading = false,
  academicYearStatus = 'active',
  isViewOnly = false,
  selectedSemester = null,
  selectedSemesterData = {},
  compact = false,
  programTypes = [],
  programs = [],
  allPrograms = [],
  allProgramTypes = [],
  hideDeleteButton = false,
  hideEditButton = false,
  selectedProgramTypeId = '',
  selectedProgramId = '',
  selectedAcademicYearId = '',
  selectedSemesterId = ''
}) => {
  
  // Function to check if current context is view-only
  const getCurrentSemesterStatus = (semester) => {
    if (!semester || typeof semester !== 'object') {
      return null;
    }
    return semester.status || null;
  };

  const currentSemesterStatus = getCurrentSemesterStatus();
  const isCompletedSemester = currentSemesterStatus === 'completed';
  
  // Check if viewing completed academic year
  const isCompletedAcademicYear = academicYearStatus === 'completed';
  
  // Determine view-only mode
  const isViewOnlyMode = isViewOnly || isCompletedAcademicYear || (isSemesterBased && isCompletedSemester);

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'No Department';
    const department = departments.find(
      dept => dept.id === departmentId || dept._id === departmentId || dept.departmentId === departmentId
    );
    return department?.name || department?.departmentName || 'Unknown Department';
  };

  const getSubjectCount = (classItem) => {
    const subjectIds = [
      ...(Array.isArray(classItem.subjects) ? classItem.subjects : []),
      ...(Array.isArray(classItem.labSubjects) ? classItem.labSubjects : [])
    ];
    const uniqueIds = [...new Set(subjectIds.map(id => id.toString()))];
    return uniqueIds.length;
  };

  const getFacultyCount = (classItem) => {
    return Array.isArray(classItem.facultyAssignments) ? classItem.facultyAssignments.length : 0;
  };

  // Filtering logic for classes
  const filteredClasses = classes.filter(classItem => {
    // Filter by program type if provided
    if (typeof selectedProgramTypeId !== 'undefined' && selectedProgramTypeId !== '' && selectedProgramTypeId !== null) {
      const classProgramTypeId = classItem.programTypeId?._id || classItem.programTypeId || '';
      if (String(classProgramTypeId) !== String(selectedProgramTypeId)) return false;
    }
    // Filter by program if provided
    if (typeof selectedProgramId !== 'undefined' && selectedProgramId !== '' && selectedProgramId !== null) {
      const classProgramId = classItem.programId?._id || classItem.programId || '';
      if (String(classProgramId) !== String(selectedProgramId)) return false;
    }
    // Filter by academic year if provided
    if (typeof selectedAcademicYearId !== 'undefined' && selectedAcademicYearId !== '' && selectedAcademicYearId !== null) {
      const classAcademicYearId = classItem.academicYearId?._id || classItem.academicYearId || '';
      if (String(classAcademicYearId) !== String(selectedAcademicYearId)) return false;
    }
    // Filter by semester if provided and class is semester-based
    if (typeof selectedSemesterId !== 'undefined' && selectedSemesterId !== '' && selectedSemesterId !== null && classItem.isSemesterBased) {
      const classSemesterId = classItem.semesterId?._id || classItem.semesterId || '';
      if (String(classSemesterId) !== String(selectedSemesterId)) return false;
    }
    return true;
  });

  const getProgramInfo = (classItem) => {
    let programTypeCode = classItem.programTypeCode || 
                         (classItem.programTypeId?.code || classItem.programTypeId?.programTypeCode) || 
                         '';
    
    let programTypeName = classItem.programTypeName || 
                         (classItem.programTypeId?.name || classItem.programTypeId?.programTypeName) || 
                         '';
    
    let programName = classItem.programName || 
                     (classItem.programId?.name || classItem.programId?.programName) || 
                     '';

    if (!programTypeCode && classItem.programTypeId) {
      const programType = allProgramTypes.find(pt => {
        const ptId = typeof pt._id === 'object' ? pt._id.$oid || pt._id.id || '' : pt._id;
        const classPtId = typeof classItem.programTypeId === 'object' ? 
                         classItem.programTypeId.$oid || classItem.programTypeId.id || '' : 
                         classItem.programTypeId;
        return String(ptId) === String(classPtId);
      });
      
      if (programType) {
        programTypeName = programType.name || programType.programTypeName || '';
        programTypeCode = programType.code || programType.programTypeCode || '';
      }
    }

    if (!programName && classItem.programId) {
      const program = allPrograms.find(prog => {
        const progId = typeof prog._id === 'object' ? prog._id.$oid || prog._id.id || '' : prog._id;
        const classProgId = typeof classItem.programId === 'object' ? 
                           classItem.programId.$oid || classItem.programId.id || '' : 
                           classItem.programId;
        return String(progId) === String(classProgId);
      });
      
      if (program) {
        programName = program.name || program.programName || '';
      }
    }

    return { 
      programTypeCode, 
      programTypeName, 
      programName,
      displayText: programTypeCode ? `${programTypeCode} • ${programName}` : programName
    };
  };

  const getSemesterInfo = (classItem) => {
    if (!classItem.isSemesterBased || !classItem.semesterId) return null;
    
    if (semesters.length > 0) {
      const foundSemester = semesters.find(sem => {
        const semId = typeof sem._id === 'object' ? sem._id.$oid || sem._id.id || '' : sem._id;
        const classSemId = typeof classItem.semesterId === 'object' ? 
                          classItem.semesterId.$oid || classItem.semesterId.id || '' : 
                          classItem.semesterId;
        return String(semId) === String(classSemId);
      });
      
      if (foundSemester) {
        return {
          name: foundSemester.semesterName || `Semester ${foundSemester.semesterNumber || ''}`,
          number: foundSemester.semesterNumber || classItem.semesterNumber,
          status: foundSemester.status || classItem.semesterStatus
        };
      }
    }
    
    return {
      name: `Semester ${classItem.semesterNumber || ''}`,
      number: classItem.semesterNumber,
      status: classItem.semesterStatus || 'active'
    };
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Loading Classes</h3>
            <p className="text-gray-500">Please wait while we fetch your class data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (filteredClasses.length === 0) {
    return (
      <div className="p-12 text-center bg-white rounded-xl border border-gray-200">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiBook className="text-2xl text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Classes Found
          </h3>
          <p className="text-gray-500 mb-6">
            {isSemesterBased 
              ? 'Get started by creating your first semester-based class.'
              : 'Get started by creating your first annual class.'
            }
          </p>
          {classes.length > 0 && !isViewOnlyMode && (
            <button
              onClick={onShowCopySection}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <FiCopy size={16} />
              Copy from Another Academic Year
            </button>
          )}
        </div>
      </div>
    );
  }

  // Determine if delete button should be shown globally
  const shouldShowDeleteButton = !isViewOnlyMode && !hideDeleteButton && !isCompletedAcademicYear;

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isSemesterBased ? 'Semester Classes' : 'Annual Classes'}
            </h2>
            <p className="text-gray-600 mt-1">
              {filteredClasses.length} class{filteredClasses.length !== 1 ? 'es' : ''} found
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {!isViewOnlyMode && (
              <button
                onClick={onShowCopySection}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm"
              >
                <FiCopy size={16} />
                Copy Classes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Classes Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {filteredClasses.map((classItem, index) => {
          const programInfo = getProgramInfo(classItem);
          const semesterInfo = getSemesterInfo(classItem);
          
          return (
            <div 
              key={classItem._id || classItem.classId || `class-${index}`}
              className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-sm"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiBook className="text-xl text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {classItem.className}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        {/* System Badge */}
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          classItem.isSemesterBased 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {classItem.isSemesterBased ? 'Semester System' : 'Annual System'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onViewDetails('classDetails', { 
                        classId: classItem._id || classItem.classId 
                      })}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <FiEye size={18} />
                    </button>
                    
                    {/* Delete button - only show if allowed */}
                    {shouldShowDeleteButton && (
                      <button
                        onClick={() => onDelete(classItem._id || classItem.classId)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Class"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiMapPin className="text-gray-400" size={14} />
                      <span className="font-medium">{getDepartmentName(classItem.departmentId)}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiLayers className="text-gray-400" size={14} />
                      <span>
                        {getSubjectCount(classItem)} subject{getSubjectCount(classItem) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiUser className="text-gray-400" size={14} />
                      <span>
                        {getFacultyCount(classItem)} faculty
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiCalendar className="text-gray-400" size={14} />
                      <span>{new Date(classItem.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Program Information */}
                {programInfo.programTypeName && programInfo.programName && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <FiCpu className="text-gray-400" size={14} />
                      <span className="text-gray-700 font-medium">
                        {programInfo.programTypeName} • {programInfo.programName}
                      </span>
                    </div>
                  </div>
                )}

                {/* Semester Info */}
                {semesterInfo && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-purple-700">
                        <FiAward className="text-purple-500" size={14} />
                        <span className="font-medium">
                          {semesterInfo.name}
                        </span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        semesterInfo.status === 'completed' 
                          ? 'bg-amber-100 text-amber-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {semesterInfo.status || 'active'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Description */}
                {classItem.description && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {classItem.description}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => onViewDetails('classDetails', { 
                      classId: classItem._id || classItem.classId 
                    })}
                    className="inline-flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  >
                    <FiEye size={14} />
                    View Details
                    <FiChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600">
          <div>
            <span>
              Showing {filteredClasses.length} of {classes.length} total classes
            </span>
          </div>
          <span className="mt-2 sm:mt-0">
            Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClassList;